import { useCallback, useEffect, useRef } from 'react';
import { useAppInfo } from './useAppInfo';

/** The wait before retry `attempt` (0-based): 2 s, 4 s, 8 s, 16 s, then every 30 s. */
export function appInfoRetryDelay(attempt: number): number {
  return Math.min(2_000 * 2 ** Math.min(attempt, 30), 30_000);
}

/**
 * Loads /api/info again, on the backoff above, for as long as the caller is
 * mounted: while a signed-in page holds behind the "can't reach" panel
 * (#2399). Success unmounts the panel, which ends the retries.
 *
 * - One call at a time: the next is scheduled only once the last has settled,
 *   so a call held by the auth-unavailable prompt does not stack more behind it.
 * - A hidden tab does not retry. Showing it again retries at once.
 *
 * Returns the panel's Retry: it calls at once and starts the backoff over.
 */
export function useAppInfoRetry(): () => void {
  const { refreshInfo } = useAppInfo();
  const retryNowRef = useRef<() => void>(() => {});

  useEffect(() => {
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let inFlight = false;
    let stopped = false;

    const clear = () => {
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
    };
    const run = () => {
      clear();
      if (stopped || inFlight) return;
      inFlight = true;
      // fire-and-forget: refreshInfo() catches and logs; the next try is scheduled once it settles
      void refreshInfo().finally(() => {
        inFlight = false;
        schedule();
      });
    };
    const schedule = () => {
      clear();
      if (stopped || inFlight || document.hidden) return;
      timer = setTimeout(run, appInfoRetryDelay(attempt++));
    };
    const onVisibilityChange = () => {
      if (document.hidden) clear();
      else run();
    };

    retryNowRef.current = () => {
      attempt = 0;
      run();
    };
    schedule();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      stopped = true;
      clear();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [refreshInfo]);

  return useCallback(() => retryNowRef.current(), []);
}
